import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { blogCategoriesApi } from "@/apis/apis";
import ImageInput from "@/components/ImageInput/Index";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import useLocaleCodes from "@/CustomHooks/useLocaleCodes";
import { getBlogCategoryInitialValues } from "@/data/BlogManagement/Categories/initial-values";
import BasicModal from "@/Modals/BasicModal";
import getBlogCategorySchema from "@/schema/BlogManagement/Categories/schema";
import { useFormik } from "formik";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";

const BlogCategoryModal = forwardRef((_, ref) => {
  // States
  const localeCodes = useLocaleCodes();
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  // Variables
  const isEdit = !!modalState.id;
  const showApi = isEdit ? `${blogCategoriesApi}/${modalState.id}` : null;

  // API Calls & Mutations
  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: isEdit,
  });
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${blogCategoriesApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: () => handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: blogCategoriesApi,
    isFormData: true,
    onSuccess: () => handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  // Functions
  const handleSuccess = (action) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Blog Category ${action} successfully.`,
    });
  };
  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    if (values.icon) formData.append("icon", values.icon);
    if (values.icon_active) formData.append("icon_active", values.icon_active);
    formData.append("status", values.status);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getBlogCategoryInitialValues(
      localeCodes,
      isEdit ? showData : null
    ),
    validationSchema: getBlogCategorySchema(localeCodes, isEdit),
    onSubmit: handleSubmit,
  });
  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  // Hooks
  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));
  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Blog Category" : "Add Blog Category"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <LocalizedInput formik={formik} error={saveError} isEdit={isEdit} />

      <Row>
        <Col md={12}>
          <ImageInput
            id="icon"
            formFormik={formik}
            fieldName="icon"
            label="Inactive Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <ImageInput
            id="icon_active"
            formFormik={formik}
            fieldName="icon_active"
            label="Active Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url_active}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default BlogCategoryModal;
